import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import CustomizedDataGrid from "../../Components/CustomizedDataGrid";
import axios from '../../../Configs/axiosInstance';

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { MAIN_URL } from "../../../Configs/Urls";
import { colorForName, initialsFor } from "./avatarUtils";
import { getStoredPageSize, setStoredPageSize } from "../../../Configs/paginationStorage";

export default function MainGrid() {
  const navigate = useNavigate();
  const [patients, setPatients] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: getStoredPageSize("patients"),
  });
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setStoredPageSize("patients", model.pageSize);
  };
  const [searchTerm, setSearchTerm] = useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [Deleteloading, setDeleteLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  const [openAddChoice, setOpenAddChoice] = React.useState(false);

  let [PatientId, setPatientId] = React.useState(null);

  // Fetch exactly one page from the backend — either the plain listing or
  // the search endpoint, depending on whether there's a search term. Both
  // return the same { data, meta } shape.
  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      const trimmedSearch = searchTerm.trim();
      const endpoint = trimmedSearch ? "patients/search" : "patients";
      const params = {
        page: paginationModel.page + 1, // backend pages are 1-indexed
        per_page: paginationModel.pageSize,
        ...(trimmedSearch ? { query: trimmedSearch } : {}),
      };

      const response = await axios.get(`${MAIN_URL}${endpoint}`, { params });

      setPatients(response.data.data);
      setRowCount(response.data.meta.total);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setError("Failed to fetch patients. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the page/page-size changes, or 450ms after the admin
  // stops typing in the search box.
  React.useEffect(() => {
    const handler = setTimeout(fetchPatients, searchTerm ? 450 : 0);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchTerm]);

  // A new search resets to page 1 — otherwise the admin can land on a page
  // that no longer exists for the narrower result set.
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleEdit = (row) => {
    navigate(`/patients/${row.id}/edit`);
  };
  const handleView = (row) => {
    navigate(`/patients/${row.id}/view`);
  };
  const handlePayment = (row) => {
    navigate(`/patients/${row.id}/payments`);
  };
  const handleDietPlans = (row) => {
    navigate(`/patients/${row.id}/diet-plans`);
  };

  const handleDelete = (row) => {
    setPatientId(row.id);
    setOpenDeleteDialog(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${MAIN_URL}patients/${PatientId}`);
      setSnackbarMessage("Patient Deleted Successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchPatients(); // re-fetch so pagination/total stays accurate
    } catch (error) {
      console.error("Error deleting patient:", error);
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error Deleting Patient. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setPatientId(null);
      setDeleteLoading(false);
    }
  };

  const addPatientOptions = [
    {
      key: "detailed",
      title: "Detailed",
      description: "Full intake form — diet, lifestyle, medical history and more. Best for a first-time registration.",
      icon: <DescriptionRoundedIcon />,
      color: "#1E88E5",
      route: "/add-patient",
    },
    {
      key: "short",
      title: "Short",
      description: "Just name, age, contact and gender. Fastest way to get a patient into the system — fill in the rest later.",
      icon: <BoltRoundedIcon />,
      color: "#F4511E",
      route: "/patients/add",
    },
  ];

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box
          onClick={(event) => {
            event.stopPropagation();
            handleView(params.row);
          }}
          sx={{ display: "flex", alignItems: "center", gap: 1.25, height: "100%", cursor: "pointer" }}
        >
          <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: colorForName(params.value) }}>
            {initialsFor(params.value)}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "age",
      headerName: "Age",
      width: 70,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "gender",
      headerName: "Gender",
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={params.value}
              size="small"
              color={params.value === "Male" ? "info" : params.value === "Female" ? "secondary" : "default"}
              variant="outlined"
            />
          </Box>
        ) : null,
    },
    {
      field: "contactNumber",
      headerName: "Contact Number",
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Added at",
      width: 190,
      renderCell: (params) => {
        const formattedDate = format(new Date(params.value), "MMM d, yyyy 'at' h:mm a");
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography variant="body2" color="text.secondary">
              {formattedDate}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="info"
              onClick={(event) => {
                event.stopPropagation();
                handleView(params.row);
              }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Payments">
            <IconButton
              size="small"
              sx={{ color: "#8E24AA" }}
              onClick={(event) => {
                event.stopPropagation();
                handlePayment(params.row);
              }}
            >
              <PaidRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Diet Plans">
            <IconButton
              size="small"
              sx={{ color: "#00897B" }}
              onClick={(event) => {
                event.stopPropagation();
                handleDietPlans(params.row);
              }}
            >
              <AssignmentRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 3 }}>
      <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
            borderRadius: 3,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Patients List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {rowCount} {rowCount === 1 ? "patient" : "patients"} in total
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
              variant="outlined"
              placeholder="Search by name or contact"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              size="medium"
              startIcon={<PersonAddAlt1RoundedIcon />}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
              onClick={() => setOpenAddChoice(true)}
            >
              Add Patient
            </Button>
          </Box>
        </Paper>

      {error && (
        <Typography component="h2" variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}>
        <CustomizedDataGrid
          rows={patients}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Add Patient — choose Detailed vs Short */}
      <Dialog
        open={openAddChoice}
        onClose={() => setOpenAddChoice(false)}
        PaperProps={{ sx: { borderRadius: 3, width: 480, maxWidth: "100%" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add a New Patient</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how much detail you want to enter right now.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {addPatientOptions.map((option) => (
              <Paper
                key={option.key}
                variant="outlined"
                onClick={() => {
                  setOpenAddChoice(false);
                  navigate(option.route);
                }}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  borderColor: "divider",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  transition: "border-color 120ms ease, background-color 120ms ease",
                  "&:hover": {
                    borderColor: option.color,
                    bgcolor: `${option.color}0D`,
                  },
                }}
              >
                <Avatar sx={{ bgcolor: `${option.color}1F`, color: option.color, width: 40, height: 40 }}>
                  {option.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {option.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
                <ChevronRightRoundedIcon sx={{ color: "text.disabled" }} />
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAddChoice(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setPatientId(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this patient?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setPatientId(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={Deleteloading}
            variant="contained"
            color="error"
            size="small"
          >
            {Deleteloading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
